import jschardet from 'jschardet';
import chardet from 'chardet';
import IconvLite from 'iconv-lite';
import { Buffer } from 'buffer';

const ZERO_BYTE_DETECTION_BUFFER_MAX_LEN = 512; // number of bytes to look at to decide about a file being binary or not

const MINIMUM_THRESHOLD = 0.2;
const IGNORE_ENCODINGS = ['ascii', 'utf-8', 'utf-16', 'utf-32'];
const JSCHARDET_TO_ICONV_ENCODINGS = {
  ibm866: 'cp866',
  big5: 'cp950',
};
function toIconvLiteEncoding(encodingName) {
  const normalizedEncodingName = encodingName.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  const mapped = JSCHARDET_TO_ICONV_ENCODINGS[normalizedEncodingName];
  return mapped || normalizedEncodingName;
}

const util = {
  checkUtf8(buffer) {
    let nBytes = 0; // UFT8可用1-6个字节编码,ASCII用一个字节
    let bAllAscii = true; // 如果全部都是ASCII, 说明不是UTF-8
    const arr = buffer;
    for (let i = 0; i < arr.length; i++) {
      const chr = arr[i];
      if ((chr & 0x80) !== 0) {
        // 判断是否ASCII编码,如果不是,说明有可能是UTF-8,ASCII用7位编码,但用一个字节存,最高位标记为0,o0xxxxxxx
        bAllAscii = false;
      }
      if (nBytes === 0) {
        // 如果不是ASCII码,应该是多字节符,计算字节数
        if (chr >= 0x80) {
          if (chr >= 0xfc && chr <= 0xfd) nBytes = 6;
          else if (chr >= 0xf8) nBytes = 5;
          else if (chr >= 0xf0) nBytes = 4;
          else if (chr >= 0xe0) nBytes = 3;
          else if (chr >= 0xc0) nBytes = 2;
          else return false;
          nBytes--;
        }
      } else {
        // 多字节符的非首字节,应为 10xxxxxx
        if ((chr & 0xc0) !== 0x80) {
          return false;
        }
        nBytes--;
      }
    }

    if (nBytes > 0) { // 违返规则
      return false;
    }
    if (bAllAscii) { // 如果全部都是ASCII, 说明不是UTF-8
      return false;
    }
    return true;
  },
  checkAscii(buffer) {
    const arr = buffer;
    for (let i = 0; i < arr.length; i++) {
      const chr = arr[i];
      if ((chr & 0x80) !== 0) {
        // 判断是否ASCII编码,如果不是,说明有可能是UTF-8,ASCII用7位编码,但用一个字节存,最高位标记为0,o0xxxxxxx
        return false;
      }
    }
    return true;
  },
  checkBom(buf) {
    return buf[0] === 0xef && buf[1] === 0xbb && buf[2] === 0xbf;
  },
  detectEncodingByBOM(buffer) {
    if (!buffer || buffer.length < 2) {
      return null;
    }

    const b0 = buffer.readUInt8(0);
    const b1 = buffer.readUInt8(1);

    // UTF-16 BE
    if (b0 === 0xfe && b1 === 0xff) {
      return 'utf16be';
    }

    // UTF-16 LE
    if (b0 === 0xff && b1 === 0xfe) {
      return 'utf16le';
    }

    if (buffer.length < 3) {
      return null;
    }

    const b2 = buffer.readUInt8(2);

    // UTF-8
    if (b0 === 0xef && b1 === 0xbb && b2 === 0xbf) {
      return 'utf8bom';
    }

    return null;
  },
  guessEncodingByBuffer(buffer) {
    if (!jschardet.Constants) jschardet.Constants = {};
    jschardet.Constants.MINIMUM_THRESHOLD = MINIMUM_THRESHOLD;

    const guessed = jschardet.detect(buffer);
    const anotherGessed = chardet.analyse(buffer);

    // {
    //   const guess = chardet.analyse(buffer)
    //   console.log({ guessed, anotherGessed }, guess);
    // }
    if (anotherGessed.length) {
      const includeGBK = anotherGessed.filter(guess => guess.name === 'GB18030').length > 0;
      if (includeGBK) {
        anotherGessed[0] = {
          name: 'gbk',
          confidence: 85,
        };
      }
      anotherGessed[0].confidence /= 100;
    }

    if (!guessed || !guessed.encoding) {
      if (!anotherGessed.length || !anotherGessed[0].name) {
        return null;
      }
    }

    const encode = anotherGessed.length && guessed.confidence < anotherGessed[0].confidence
      ? anotherGessed[0].name
      : guessed.encoding;
    // Ignore encodings that cannot guess correctly
    // (http://chardet.readthedocs.io/en/latest/supported-encodings.html)
    if (IGNORE_ENCODINGS.indexOf(encode.toLowerCase()) >= 0) {
      return null;
    }

    return toIconvLiteEncoding(encode);
  },
};

const checker = {
  ascii: util.checkAscii,
  gbk: buf => !util.checkBom(buf) && !util.checkAscii(buf) && !util.checkUtf8(buf),
  utf8: buf => !util.checkBom(buf) && !util.checkAscii(buf) && util.checkUtf8(buf),
  utf8bom: buf => util.checkBom(buf) && !util.checkAscii(buf) && util.checkUtf8(buf),
  utf16be: buf => buf[0] === 0xfe && buf[1] === 0xff,
  utf16le: buf => buf[0] === 0xff && buf[1] === 0xfe,
};

const mod = {
  detectEncodingFromBuffer(arrayBuf) {
    const buffer = Buffer.from(arrayBuf);
    let encoding = util.detectEncodingByBOM(buffer);

    if (encoding) {
      return encoding;
    }

    let seemsBinary = false;
    if (encoding !== 'utf16be' && encoding !== 'utf16le' && buffer) {
      let couldBeUTF16LE = true; // e.g. 0xAA 0x00
      let couldBeUTF16BE = true; // e.g. 0x00 0xAA
      let containsZeroByte = false;

      // 再次检测 buffer 的 512 字节中是否有字节为 0，则编码类型可能不是 utf-16
      for (let i = 0; i < buffer.length && i < ZERO_BYTE_DETECTION_BUFFER_MAX_LEN; i++) {
        const isEndian = i % 2 === 1; // assume 2-byte sequences typical for UTF-16
        const isZeroByte = buffer.readInt8(i) === 0;

        if (isZeroByte) {
          containsZeroByte = true;
        }

        // UTF-16 LE: expect e.g. 0xAA 0x00
        if (couldBeUTF16LE && ((isEndian && !isZeroByte) || (!isEndian && isZeroByte))) {
          couldBeUTF16LE = false;
        }

        // UTF-16 BE: expect e.g. 0x00 0xAA
        if (couldBeUTF16BE && ((isEndian && isZeroByte) || (!isEndian && !isZeroByte))) {
          couldBeUTF16BE = false;
        }

        // Return if this is neither UTF16-LE nor UTF16-BE and thus treat as binary
        if (isZeroByte && !couldBeUTF16LE && !couldBeUTF16BE) {
          break;
        }
      }

      // Handle case of 0-byte included
      if (containsZeroByte) {
        if (couldBeUTF16LE) {
          encoding = 'utf16be';
        } else if (couldBeUTF16BE) {
          encoding = 'utf16le';
        } else {
          seemsBinary = true;
        }
      }

      // if (checker.gbk(buffer)) {
      //   encoding = 'gbk';
      // } else
      if (checker.utf8(buffer)) {
        encoding = 'utf8';
      }

      if (!seemsBinary && !encoding && buffer) {
        return util.guessEncodingByBuffer(buffer);
      }

      return encoding;
    }
    return encoding;
  },
  toStr(arrayBuf, charset) {
    const buf = Buffer.from(arrayBuf);
    let content;
    if (charset === 'utf8') {
      content = buf.toString('utf8');
    } else if (charset === 'utf8bom') {
      if (util.checkBom(buf)) {
        content = buf.slice(3).toString('utf8');
      } else {
        content = buf.toString('utf8');
      }
    } else {
      // icon-lite
      try {
        content = IconvLite.decode(buf, charset);
      } catch (e) {
        console.log(`[charset] IconvLite error from ${charset} to utf8`);
      }

      // // encoding
      // try {
      //     content = encoding.convert(buf, 'utf8', charset).toString();
      // } catch (e) {
      //     console.log(`[charset] encoding error from ${charset} to utf8`);
      // }

      // // TextDecoder
      // try {
      //     content = new TextDecoder(charset).decode(buf);
      // } catch (e) {
      //     console.log(`[charset] TextDecoder error from ${charset} to utf8`);
      //     try {
      //         content = encoding.convert(buf, 'utf8', charset).toString();
      //     } catch (e) {
      //         console.log(`[charset] encoding error from ${charset} to utf8`);
      //     }
      // }
    }
    console.log(`[charset] ${charset} to utf8`);
    return content;
  },
  toBuf(str, charset) {
    let buf = false;
    if (charset === 'utf8' || charset === 'utf8bom') {
      buf = Buffer.from(str, 'utf8');
    } else {
      // icon-lite
      try {
        buf = IconvLite.encode(str, charset);
      } catch (e) {
        console.log(`[charset] IconvLite error from utf8 to ${charset}`);
      }
      // // encoding
      // // 由于 TextEncoder为了符合编码，只支持默认编码 utf8，去掉了 charset 参数，这里用 encoding 代替
      // try {
      //     buf = encoding.convert(str, charset, 'utf8');
      // } catch (e) {
      //     console.log(`[charset] encoding error from utf8 to ${charset}`);
      // }
    }
    if (charset === 'utf8bom' && !checker.utf16be(buf)) buf = Buffer.concat([Buffer.from([0xef, 0xbb, 0xbf]), buf]);
    if (charset === 'utf16be' && !checker.utf16be(buf)) buf = Buffer.concat([Buffer.from([0xfe, 0xff]), buf]);
    if (charset === 'utf16le' && !checker.utf16le(buf)) buf = Buffer.concat([Buffer.from([0xff, 0xfe]), buf]);
    console.log(`[charset] utf8 to ${charset}`);
    return buf;
  },
};

export default mod;
